
import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCcw, Bug } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class DebugErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 md:p-8 font-sans">
          <div className="max-w-4xl w-full bg-white rounded-2xl shadow-2xl border border-red-200 overflow-hidden">
            {/* Header */}
            <div className="bg-red-600 px-8 py-6 flex items-center gap-4">
               <div className="bg-white/20 p-3 rounded-full">
                    <AlertTriangle className="text-white w-8 h-8" />
               </div>
               <div>
                   <h1 className="text-2xl font-bold text-white">Application Error</h1>
                   <p className="text-red-100 opacity-90">Hệ thống gặp lỗi nghiêm trọng và không thể hiển thị.</p>
               </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-6">
                
                {/* Error Message */}
                <div>
                    <h2 className="font-bold text-slate-800 flex items-center gap-2 mb-2">
                        <Bug className="w-5 h-5 text-red-500"/>
                        Thông báo lỗi (Error Message):
                    </h2>
                    <div className="bg-red-50 p-4 rounded-xl border border-red-100 text-red-700 font-mono text-sm break-words whitespace-pre-wrap shadow-inner">
                        {this.state.error?.toString() || "Unknown Error"}
                    </div>
                </div>

                {/* Stack Trace */}
                {this.state.errorInfo && (
                    <div>
                        <h2 className="font-bold text-slate-800 mb-2">Vị trí lỗi (Stack Trace):</h2>
                        <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 text-green-400 font-mono text-xs overflow-auto h-64 shadow-inner custom-scrollbar">
                            {this.state.errorInfo.componentStack}
                        </div>
                        <p className="text-xs text-slate-400 mt-2 italic">
                            * Hãy chụp màn hình khu vực này và gửi cho Developer để được hỗ trợ nhanh nhất.
                        </p>
                    </div>
                )}

                {/* Actions */}
                <div className="pt-4 border-t border-slate-100 flex justify-end gap-4">
                    <button 
                        onClick={() => {
                            localStorage.clear();
                            window.location.href = '/';
                        }}
                        className="px-6 py-3 text-slate-600 font-bold hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        Xoá Cache & Reset
                    </button>
                    <button 
                        onClick={() => window.location.reload()}
                        className="px-6 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 shadow-lg shadow-red-500/30"
                    >
                        <RefreshCcw className="w-4 h-4" /> Tải lại trang
                    </button>
                </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
